/**
 * Nachfassen an Angeboten — reine Logik hinter dem Reiter "Nachfassen".
 *
 * Bisher war ein Nachfassen ein einzelner Klick auf "Erledigt": er setzte
 * follow_up_done_at und damit war das Angebot aus der Liste verschwunden. Was
 * beim Kunden herauskam, wer telefoniert hat und wann erneut gefragt werden
 * soll, stand nirgends — beim zweiten Anruf begann man wieder bei null.
 *
 * Deshalb schreibt jedes erledigte Nachfassen jetzt einen Eintrag in
 * QuoteDetails.followUpLog (Stand, Nachfassender, naechster Termin) und setzt
 * den naechsten Zyklus. Zwei Ausgaenge:
 *
 *  - naechster Termin gesetzt -> follow_up_date = neuer Termin,
 *    follow_up_done_at bleibt LEER (das Angebot ist nicht fertig nachgefasst,
 *    es ist nur bis dahin nicht faellig).
 *  - "kein weiteres Nachfassen" -> follow_up_done_at gesetzt, das Angebot
 *    verlaesst den Reiter dauerhaft.
 *
 * Die Faelligkeit selbst bleibt an denselben drei Feldern haengen, die
 * fetchDueFollowUps und computeQuoteStats lesen (status 'sent',
 * follow_up_done_at leer, follow_up_date <= heute) — der Verlauf steuert nie
 * mit, sonst zaehlen Kachel und Reiter wieder verschieden.
 */

import { Quote, QuoteDetails, QuoteFollowUpEntry, QuoteFollowUpOutcome, QuoteStatus } from '../types';
import { computeFollowUpDate } from './quoteLogic';

/**
 * Status, in denen nachgefasst wird: versendet (Entscheidung offen) und
 * TEILBEAUFTRAGT (der Rest ist offen — „Rest nachfassen"). Dieselbe Liste
 * lesen followUpDueState, fetchDueFollowUps und computeQuoteStats.
 */
export const FOLLOW_UP_STATUSES: readonly QuoteStatus[] = ['sent', 'partial'];

export const FOLLOW_UP_OUTCOMES: { key: QuoteFollowUpOutcome; label: string }[] = [
    { key: 'reached', label: 'Kunde erreicht' },
    { key: 'no_answer', label: 'Nicht erreicht' },
    { key: 'decision_pending', label: 'Entscheidung offen' },
    { key: 'postponed', label: 'Kunde hat vertagt' },
    { key: 'closed', label: 'Kein weiteres Nachfassen' },
];

const OUTCOME_LABEL: Record<QuoteFollowUpOutcome, string> =
    FOLLOW_UP_OUTCOMES.reduce((acc, o) => ({ ...acc, [o.key]: o.label }), {} as Record<QuoteFollowUpOutcome, string>);

export const followUpOutcomeLabel = (outcome: QuoteFollowUpOutcome): string =>
    OUTCOME_LABEL[outcome] || outcome;

/** Verlauf eines Angebots, aeltester Eintrag zuerst. Nie undefined. */
export const followUpLogOf = (q: Pick<Quote, 'details'>): QuoteFollowUpEntry[] =>
    Array.isArray(q.details?.followUpLog) ? q.details!.followUpLog! : [];

/** Der zuletzt erfasste Stand, oder null wenn noch nie nachgefasst wurde. */
export const lastFollowUp = (q: Pick<Quote, 'details'>): QuoteFollowUpEntry | null => {
    const log = followUpLogOf(q);
    return log.length ? log[log.length - 1] : null;
};

/** Wie oft wurde an diesem Angebot schon nachgefasst? */
export const followUpCount = (q: Pick<Quote, 'details'>): number => followUpLogOf(q).length;

/**
 * Naechster Termin aus einem Abstand in Tagen, gerechnet ab heute.
 * 0 oder negativ ergibt heute — ein Termin in der Vergangenheit waere sofort
 * wieder faellig und die Nachfrage damit nie beantwortet.
 */
export const nextFollowUpFromDays = (todayIso: string, days: number): string => {
    const safe = Number.isFinite(days) && days > 0 ? Math.floor(days) : 0;
    return computeFollowUpDate(`${todayIso.slice(0, 10)}T00:00:00.000Z`, safe);
};

export type FollowUpDueState = 'overdue' | 'today' | 'upcoming' | 'none';

/** Faelligkeit eines Angebots rein am Datum — ohne Verlauf, ohne Status. */
export const followUpDueState = (
    q: Pick<Quote, 'status' | 'followUpDate' | 'followUpDoneAt'>,
    todayIso: string,
): FollowUpDueState => {
    if (!FOLLOW_UP_STATUSES.includes(q.status) || q.followUpDoneAt || !q.followUpDate) return 'none';
    const due = q.followUpDate.slice(0, 10);
    const today = todayIso.slice(0, 10);
    if (due < today) return 'overdue';
    if (due === today) return 'today';
    return 'upcoming';
};

/** Offen = faellig oder terminiert. Das ist der Bestand des Reiters. */
export const isFollowUpOpen = (
    q: Pick<Quote, 'status' | 'followUpDate' | 'followUpDoneAt'>,
    todayIso: string,
): boolean => followUpDueState(q, todayIso) !== 'none';

export interface FollowUpInput {
    outcome: QuoteFollowUpOutcome;
    note: string;
    by?: string;
    byName: string;
    /** Naechster Termin (yyyy-mm-dd). Leer = Nachfassen abschliessen. */
    nextDate?: string;
    nextIntervalDays?: number;
    /** Zeitpunkt der Erfassung; nur fuer Tests zu setzen. */
    at?: string;
}

export interface FollowUpRecord {
    entry: QuoteFollowUpEntry;
    /** Vollstaendiger, neuer Verlauf (bestehender + Eintrag). */
    log: QuoteFollowUpEntry[];
    /** Neues Nachfassdatum, oder das bisherige beim Abschliessen. */
    followUpDate?: string;
    /** Gesetzt = kein weiteres Nachfassen. */
    followUpDoneAt?: string;
    /** true = Nachfassen laeuft weiter (neuer Termin). */
    continues: boolean;
}

/**
 * Baut Verlaufseintrag und Folgezustand aus der Eingabe des Dialogs.
 * Kein Seiteneffekt — geschrieben wird in crmService.recordFollowUp.
 */
export const buildFollowUpRecord = (
    quote: Pick<Quote, 'details' | 'followUpDate'>,
    input: FollowUpInput,
): FollowUpRecord => {
    const at = input.at || new Date().toISOString();
    const next = (input.nextDate || '').slice(0, 10) || undefined;
    const entry: QuoteFollowUpEntry = {
        at,
        dueDate: quote.followUpDate?.slice(0, 10),
        by: input.by,
        byName: input.byName,
        outcome: input.outcome,
        note: input.note.trim(),
        nextDate: next,
        nextIntervalDays: next ? input.nextIntervalDays : undefined,
    };
    return {
        entry,
        log: [...followUpLogOf(quote), entry],
        // Ohne Folgetermin bleibt das bisherige Datum stehen: es datiert den
        // abgeschlossenen Zyklus und ist die Bezugsgroesse des Verlaufs.
        followUpDate: next ?? quote.followUpDate?.slice(0, 10),
        followUpDoneAt: next ? undefined : at,
        continues: Boolean(next),
    };
};

/**
 * details fuer das Update — der Verlauf wird an den FRISCH gelesenen Stand
 * angehaengt, nie an eine alte Kopie im Speicher. Sonst nimmt ein Nachfassen
 * einen parallel im Editor geaenderten Textbaustein wieder zurueck.
 */
export const mergeFollowUpLog = (
    serverDetails: QuoteDetails | undefined,
    entry: QuoteFollowUpEntry,
): QuoteDetails => {
    const base = serverDetails || {};
    const existing = Array.isArray(base.followUpLog) ? base.followUpLog : [];
    // Doppelklick / Wiederholung nach Netzfehler: derselbe Zeitstempel desselben
    // Nutzers darf nicht zweimal im Verlauf stehen.
    if (existing.some((e) => e.at === entry.at && e.by === entry.by)) return base;
    return { ...base, followUpLog: [...existing, entry] };
};
